import { useMemo, useState } from "react";
import { CalendarClock, Inbox, MessagesSquare, PauseCircle, RefreshCw, Settings2, BarChart3, Users } from "lucide-react";
import { useEmailWorkspace } from "@/components/email/use-email-workspace";
import AssistantPanel from "@/components/email/assistant-panel";
import QueuePanel from "@/components/email/queue-panel";
import SchedulePanel from "@/components/email/schedule-panel";
import PeoplePanel from "@/components/email/people-panel";
import ReportsPanel from "@/components/email/reports-panel";
import SettingsPanel from "@/components/email/settings-panel";
import type { DraftInput } from "@/components/email/api";
import { Notice, Skeleton, btnGhost } from "@/components/email/shared";

const TABS = [
  { id: "assistant", label: "Assistant", icon: MessagesSquare },
  { id: "approvals", label: "Approvals", icon: Inbox },
  { id: "schedule", label: "Schedule", icon: CalendarClock },
  { id: "people", label: "People", icon: Users },
  { id: "reports", label: "Reports", icon: BarChart3 },
  { id: "settings", label: "Settings", icon: Settings2 },
] as const;
type TabId = (typeof TABS)[number]["id"];

function readParams() {
  const p = new URLSearchParams(window.location.search);
  const raw = p.get("tab");
  const tab: TabId = raw === "compose" ? "assistant" : (TABS.find((t) => t.id === raw)?.id ?? "assistant");
  const email = p.get("email")?.trim() || undefined;
  const cohort = Number(p.get("cohortId"));
  return { tab, email, cohortId: Number.isInteger(cohort) && cohort > 0 ? cohort : undefined, compose: raw === "compose" };
}

export default function AdminEmail() {
  const params = useMemo(readParams, []);
  const { data, error, loading, refresh } = useEmailWorkspace();
  const [tab, setTab] = useState<TabId>(params.tab);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [seed, setSeed] = useState<{ key: number; value?: Partial<DraftInput> }>({
    key: 0, value: params.compose || params.cohortId ? { to: params.email ? [params.email] : [], cohortId: params.cohortId } : undefined,
  });

  const go = (t: TabId) => { setTab(t); const u = new URL(window.location.href); u.searchParams.set("tab", t); u.searchParams.delete("email"); window.history.replaceState(null, "", u.toString()); };
  const openMessage = (id: number) => { setSelectedId(id); go("approvals"); };
  const prepareFor = (email: string) => { setSeed((s) => ({ key: s.key + 1, value: { to: [email] } })); go("assistant"); };

  const reviewCount = data?.messages.filter((m) => m.status === "draft" || m.status === "needs_attention").length || 0;

  return (
    <div className="email-module space-y-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF5C00]">Central outbox</p>
          <h1 className="mt-1 font-serif text-3xl sm:text-4xl tracking-tight">Email</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground leading-relaxed">One place for Select emails. Review and approve non-essential messages before they send. Account and operational messages run automatically and are recorded here.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data?.settings.paused && <button data-testid="chip-paused" onClick={() => go("settings")} className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-800 dark:text-amber-200"><PauseCircle className="w-3.5 h-3.5" />Marketing paused</button>}
          {data && <button data-testid="chip-connection" onClick={() => go("settings")} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${data.connection.configured ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200"}`}><span className={`h-1.5 w-1.5 rounded-full ${data.connection.configured ? "bg-emerald-500" : "bg-amber-500"}`} />{data.connection.configured ? "Resend configured" : data.connection.status === "not_configured" ? "Resend needs attention" : "Resend check unavailable"}</button>}
          <button data-testid="button-refresh" onClick={() => void refresh()} className="p-2 rounded-md hover:bg-muted text-muted-foreground" aria-label="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </header>

      <nav className="overflow-x-auto overflow-y-hidden border-b border-border" aria-label="Email sections">
        <ul className="flex min-w-max gap-1 px-2">
          {TABS.map((t) => {
            const Icon = t.icon; const active = tab === t.id;
            return (
              <li key={t.id}>
                <button data-testid={`tab-${t.id}`} onClick={() => go(t.id)} className={`relative inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium transition-colors ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <Icon className={`w-4 h-4 ${active ? "text-[#FF5C00]" : ""}`} />{t.label}
                  {t.id === "approvals" && reviewCount > 0 && <span className="rounded-full bg-[#FF5C00] px-1.5 text-[10px] font-semibold leading-4 text-white">{reviewCount}</span>}
                  {active && <span className="absolute inset-x-2 -bottom-px h-0.5 bg-[#FF5C00]" />}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {error && !data && <div className="space-y-3"><Notice tone="error">{error}</Notice><button data-testid="button-retry" className={btnGhost} onClick={() => void refresh()}><RefreshCw className="w-4 h-4" />Try again</button></div>}
      {error && data && <Notice tone="warn">Last refresh failed: {error}. Showing the previous data.</Notice>}
      {loading && !data && !error && <Skeleton rows={6} />}

      {data && (
        <div>
          {tab === "assistant" && <AssistantPanel key={seed.key} chat={data.chat} cohorts={data.cohorts} seed={seed.value} onRefresh={refresh} onSaved={(id) => { void refresh(); openMessage(id); }} />}
          {tab === "approvals" && <QueuePanel messages={data.messages} cohorts={data.cohorts} selectedId={selectedId} onSelect={setSelectedId} onChanged={() => void refresh()} />}
          {tab === "schedule" && <SchedulePanel messages={data.messages} settings={data.settings} onOpen={openMessage} />}
          {tab === "people" && <PeoplePanel initialEmail={params.email} onPrepare={prepareFor} />}
          {tab === "reports" && <ReportsPanel data={data} onOpen={openMessage} />}
          {tab === "settings" && <SettingsPanel data={data} onSaved={() => void refresh()} />}
        </div>
      )}
    </div>
  );
}
